import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useJobListMap } from "../hooks/useJobListMap";
import useChat from "../hooks/useChat";
import { IChatContextV2 } from "../types/ChatInterfaces";
import { useParams } from "react-router-dom";

// Define the context type
interface JobListMapContextType {
  state: ReturnType<typeof useJobListMap>["state"];
  dispatch: ReturnType<typeof useJobListMap>["dispatch"];
  fetchData: (params: any) => Promise<any>;
  loading: boolean;
  viewMode: "list" | "map";
  setViewMode: (viewMode: "list" | "map") => void;
  allowedViewModes: ("list" | "map")[];
  updateViewModeData: (data: {
    viewMode?: "list" | "map";
    allowedViewModes?: ("list" | "map")[];
  }) => void;
  fetchMoreJobs: ReturnType<typeof useJobListMap>["fetchMoreJobs"];
  isLoadingMore: ReturnType<typeof useJobListMap>["isLoadingMore"];
  showAllFilters: boolean;
  setShowAllFilters: (showAllFilters: boolean) => void;
  reset: () => void;
}

// Create the context
const JobListMapContext = createContext<JobListMapContextType | undefined>(
  undefined
);

// Provider component
export const JobListMapProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [viewModeData, setViewModeData] = useState({
    viewMode: "list" as "list" | "map",
    allowedViewModes: ["list", "map"] as ("list" | "map")[],
  });

  const {
    bot_id: IndependentChatBotId,
    conv_id: IndependentChatConvId,
    message_id: parentMessageId,
  } = useParams();
  const chatContext = useChat() as IChatContextV2;
  const config = chatContext?.config || {};
  const botId = IndependentChatBotId || config.botId || "";
  const convId = IndependentChatConvId || chatContext.convId || "";

  const { state, dispatch, fetchData, loading, fetchMoreJobs, isLoadingMore } =
    useJobListMap(botId, convId);

  const setViewMode = useCallback((newViewMode: "list" | "map") => {
    setViewModeData((prev) => ({
      ...prev,
      viewMode: newViewMode,
    }));
  }, []);

  const updateViewModeData = useCallback(
    (data: {
      viewMode?: "list" | "map";
      allowedViewModes?: ("list" | "map")[];
    }) => {
      setViewModeData((prev) => ({
        ...prev,
        ...data,
      }));
    },
    []
  );

  const reset = useCallback(() => {
    // Reset the job list map state to initial values
    dispatch({ type: "RESET" });

    // Reset view mode data to initial values
    setViewModeData({
      viewMode: "list",
      allowedViewModes: ["list", "map"],
    });

    // Reset filters visibility
    setShowAllFilters(false);
  }, [dispatch]);

  return (
    <JobListMapContext.Provider
      value={{
        state,
        dispatch,
        fetchData,
        loading,
        viewMode: viewModeData.viewMode,
        setViewMode,
        allowedViewModes: viewModeData.allowedViewModes,
        updateViewModeData,
        fetchMoreJobs,
        isLoadingMore,
        showAllFilters,
        setShowAllFilters,
        reset,
      }}
    >
      {children}
    </JobListMapContext.Provider>
  );
};

// Custom hook for consuming the context
export const useJobListMapContext = () => {
  const context = useContext(JobListMapContext);
  if (!context) {
    throw new Error(
      "useJobListMapContext must be used within a JobListMapProvider"
    );
  }
  return context;
};
